import { useSelector , useDispatch } from 'react-redux';

import { type PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

import useGroupApi from '../data/groupApi';
import { EnumRelation, type Group } from '../domain/group';
import { type Packet } from '../domain/packet';

import type { RootState } from '#root//rootState';
import { setLoader, unsetLoader } from '@/common/application/toastSlice';
import { logException, logTrace } from '@/common/utils/logger';


interface fullGroup extends Group {
    hasAllMembers: boolean
}

const samePacket = (a: Packet, b: Packet): boolean => {
    if (a.documentId && b.documentId) return a.documentId === b.documentId;
    if (a.id !== undefined && b.id !== undefined) return String(a.id) === String(b.id);
    return false;
};

/**
 * Keep a deeply-resolved packet config alive across a shallower refetch.
 *
 * Strapi's `populate: '*'` only populates ONE level, so a fetch asking for `*` (e.g.
 * `apiGetAllGroupAdminsRT`) returns a group's `packet` WITHOUT its nested `config`. Cached wholesale,
 * that erases the `packet.config.features` a deep fetch had already resolved — silently flipping
 * feature gates such as `enableAccessProfile` off, which makes the "My access" nav entry vanish
 * mid-session. So when the SAME packet comes back without a config, carry the known one over.
 *
 * The incoming packet still wins for everything it does carry, and a packet that is absent from the
 * payload still overwrites (an unlinked packet must be able to clear).
 */
const mergePacket = (incoming?: Packet, existing?: Packet): Packet | undefined => {
    if (!incoming || incoming.config || !existing?.config) return incoming;
    return samePacket(incoming, existing) ? { ...incoming, config: existing.config } : incoming;
};
export interface GroupStore {
    
    contextGroupMap: {[key: string]: fullGroup},
    isLoading: boolean,
    preloader: {
        admins: Group[] | null,
        parkstorySuperAdmin: Group[] | null
    },
    keyLoader: {[key: string]: Promise<fullGroup>}
}

const initialState: GroupStore = {
    contextGroupMap: {},
    isLoading: false,
    keyLoader: {},
    preloader: {
        admins: null,
        parkstorySuperAdmin: null
    }
    
};


export interface SelfResolvingPromise<T> extends Promise<T> {
    resolve: (value: T) => void,
    reject: (reason?: any) => void
}
export function createEmptyPromise<T>() {
    let resolve: (value: T) => void;
    let reject: (reason?: any) => void;
  
    const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
            
       
    }) as SelfResolvingPromise<T>;
    promise.resolve = resolve;
    promise.reject = reject;

    return promise;

}
const sideState = {
    admKeyLoader: {} as {[key: string]: SelfResolvingPromise<fullGroup>},
    keyLoader: {} as {[key: string]: SelfResolvingPromise<fullGroup>},
    inheritanceLoader: {} as {[key: string]: SelfResolvingPromise<fullGroup[]>},
    superAdminLoader: null as Promise<Group[] | null> | null,
};
  
const groupStore = createSlice({
    initialState,
    name: 'groupStore',
    reducers: {
        clearStore() {
            return initialState;
        },
        deleteGroup(state, action: PayloadAction<number | string>) {
            if (state.contextGroupMap[String(action.payload)]) delete state.contextGroupMap[String(action.payload)];
        },
        setAdmLoadingKey(state, action: PayloadAction<string>) {
            sideState.admKeyLoader[action.payload] = createEmptyPromise();
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload;
        },
        setLoadingKey(state, action: PayloadAction<string>) {
            sideState.keyLoader[action.payload] = createEmptyPromise();
        },
        setPreLoading(state, action: PayloadAction<{ key: 'admins' | 'parkstorySuperAdmin', value: Group[]}>) {
            state.preloader[action.payload.key] = action.payload.value;
        },
        unsetAdmLoadingKey(state, action: PayloadAction<{key: string, grp: Group}>) {
            if (action.payload.key) {
                sideState.admKeyLoader[action.payload.key].resolve({...action.payload.grp, hasAllMembers: true});
                delete sideState.admKeyLoader[action.payload.key];
            }
        },
        upsertGroup(state, action: PayloadAction<Group>) {
            const grp = action.payload;
            if (grp.documentId) {
                const existing = state.contextGroupMap[grp.documentId];
                const merged = {
                    ...existing,
                    ...grp,
                    hasAllMembers: true,
                    relatesTo: (grp.relatesTo && grp.relatesTo.length > 0) ? grp.relatesTo : (existing?.relatesTo ?? grp.relatesTo),
                    packet: mergePacket(grp.packet, existing?.packet),
                };
                state.contextGroupMap[grp.documentId] = merged;
                if (typeof sideState.keyLoader[grp.documentId] !== 'undefined') {
                    sideState.keyLoader[grp.documentId].resolve(merged);
                    delete sideState.keyLoader[grp.documentId];
                }
            }
        },
        upsertGroups(state, action: PayloadAction<Group[]>) {
            const grpMap:{[key: string]: fullGroup} = {};
            for (const grp of action.payload) {
                if (grp.documentId) {
                    const existing = state.contextGroupMap[grp.documentId];
                    grpMap[grp.documentId] = {
                        ...grp,
                        hasAllMembers: false,
                        packet: mergePacket(grp.packet, existing?.packet),
                    };
                }
            }
            state.contextGroupMap = { ...state.contextGroupMap, ...grpMap};
        }
 
    },
    selectors: {
        enterpriseGroups: createSelector([state => state.groupStore.contextGroupMap], (contextGroupMap): fullGroup[] => {
            return Object.values<fullGroup>(contextGroupMap ?? {})?.filter((grp: fullGroup) => grp?.groupConfigs?.some(config => config?.groupConfig.__component === 'user.enterprise')) ?? [];
        }),
        enterpriseGroupsByParking: createSelector([state => state.groupStore.contextGroupMap], (contextGroupMap): (id: string | number) => fullGroup[] => {
            const enterprises = Object.values<fullGroup>(contextGroupMap ?? {}).filter((grp: fullGroup) => grp.groupConfigs?.some(config => config.groupConfig.__component === 'user.enterprise'));
            return (id: string | number) => enterprises.filter(grp => grp.relatesTo?.some(
                relate => (
                    relate.relation == EnumRelation.INHERITANCE 
                    && relate.targetId === String(id)
                    && relate.targetModel === 'parking'
                ))
            ) ?? [];
        }),
        getAdminById: createSelector([state => state.groupStore.contextGroupMap], (contextGroupMap): (id: string | number) => fullGroup | undefined => {
            return (id: string | number) => (Object.values<fullGroup>(contextGroupMap ?? {})
                .filter(grp => grp.relatesTo?.some(
                    relate => (
                        relate.relation == EnumRelation.OWNERSHIP 
                    && relate.targetId === String(id) 
                    && relate.targetModel === 'userGroup'
                    )
                )
                ) ?? []).at(0);
        }),
        getById: createSelector([state => state.groupStore.contextGroupMap], (contextGroupMap): (id: string | number) => fullGroup | null => {
            return (id: string | number) => contextGroupMap ? contextGroupMap[String(id)] : null;
        }),
        inheritedGroupBySuper: createSelector([state => state.groupStore.contextGroupMap], (contextGroupMap): (id: string | number) => fullGroup[] => {
            return (id: string | number) => (Object.values<fullGroup>(contextGroupMap ?? {}).filter(
                (grp: fullGroup) => grp.relatesTo?.some(relate => (
                    relate.relation == EnumRelation.INHERITANCE 
                    && relate.targetId === String(id) 
                    && relate.targetModel === 'userGroup'
                )
                    
                )) ?? []);
        }),

    }
});


export const { clearStore, upsertGroups, setAdmLoadingKey, unsetAdmLoadingKey, setLoadingKey, setPreLoading, setLoading, upsertGroup, deleteGroup } = groupStore.actions;
export const { enterpriseGroups, enterpriseGroupsByParking, getAdminById, getById, inheritedGroupBySuper } = groupStore.selectors;
export default groupStore.reducer;


export function useGroupStore() {
    
    const groupStoreState = useSelector(((state: RootState) => state.groupStore));
    const dispatch = useDispatch();
    const { apiGetGroup, apiGetGroupAdminsRT, apiGetCompanies, apiGetSubGroupsByInheritanceRT, apiGetAllGroupAdminsRT, apiGetGroupsByPrefix } = useGroupApi();
    const { enterpriseGroups, enterpriseGroupsByParking, getAdminById, getById, inheritedGroupBySuper } = groupStore.getSelectors();
    const getEnterpriseGroups = useSelector(enterpriseGroups);
    const getEnterpriseGroupsByParking = useSelector(enterpriseGroupsByParking);
    const findAdminById = useSelector(getAdminById);
    const findById = useSelector(getById);
    const getInheritedGroupBySuper = useSelector(inheritedGroupBySuper);
    return {
     
        async deleteGroup(id: number | string) {
            return dispatch(deleteGroup(id));
        },
        async enterpriseGroups(force = false) {
            if ((force || getEnterpriseGroups.length < 1) && !groupStoreState.isLoading) {
                dispatch(setLoading(true));
                dispatch(setLoader());
                try {
                    const groupsResp = await apiGetCompanies(true);
                    if (groupsResp.status.code < 400) {
                        dispatch(upsertGroups(groupsResp.data));
                        dispatch(setLoading(false));
                        dispatch(unsetLoader());
                        return groupsResp.data;
                    } else {
                        dispatch(setLoading(false));
                        dispatch(unsetLoader());
                    }
                } catch (_) {
                    dispatch(setLoading(false));
                    dispatch(unsetLoader());
                }
            }

            return getEnterpriseGroups;
        },
        async enterpriseGroupsByParking(id: string | number, force = false) {
            if ((force || getEnterpriseGroups.length < 1) && !groupStoreState.isLoading) {
                dispatch(setLoading(true));
                dispatch(setLoader());
                try {
                    const groupsResp = await apiGetCompanies(true);
                    if (groupsResp.status.code < 400) {
                        dispatch(upsertGroups(groupsResp.data));
                        dispatch(setLoading(false));
                        dispatch(unsetLoader());
                        return groupsResp.data;
                    } else {
                        dispatch(setLoading(false));
                        dispatch(unsetLoader());
                    }
                } catch (_) {
                    dispatch(setLoading(false));
                    dispatch(unsetLoader());
                }
            }


            return getEnterpriseGroupsByParking(id);
        },
        async getAdminById(documentId: string) : Promise<Group> {
            logTrace('getAdminById', undefined, { documentId });
            try {
                const found = findAdminById(documentId);

                // If the group is not found and not already being loaded, fetch it from the API
                if (!found && typeof sideState.admKeyLoader[documentId] === 'undefined') {
                    dispatch(setAdmLoadingKey(documentId));
                    dispatch(setLoader());
                    try {
                        const groupResp = await apiGetGroupAdminsRT(documentId);

                        // If the group is found and not already being loaded, fetch the full group details
                        if (groupResp?.documentId && typeof sideState.keyLoader[groupResp.documentId] === 'undefined') {

                            dispatch(setLoadingKey(groupResp.documentId));
                            // apiGetGroup returns Group & { fleet: Fleet } | null directly (no .data wrapper)
                            const fullGroupResp = await apiGetGroup(groupResp.documentId);
                            if (fullGroupResp?.documentId) {
                                dispatch(upsertGroup(fullGroupResp));
                                dispatch(unsetAdmLoadingKey({ grp: fullGroupResp, key: documentId }));
                                dispatch(unsetLoader());
                            } else {
                                // Fallback: cache with lightweight data from apiGetGroupAdminsRT
                                dispatch(upsertGroup(groupResp));
                                dispatch(unsetAdmLoadingKey({ grp: groupResp, key: documentId }));
                                dispatch(unsetLoader());
                            }
                            return fullGroupResp ?? groupResp;
                        } else if (groupResp?.documentId && typeof sideState.keyLoader[groupResp.documentId] !== 'undefined') {
                            dispatch(unsetLoader());
                            const grpAd = await sideState.keyLoader[groupResp.documentId];
                            dispatch(unsetAdmLoadingKey({ grp: grpAd, key: documentId }));

                            return await sideState.keyLoader[documentId];
                        } else {
                            dispatch(unsetLoader());
                            dispatch(unsetAdmLoadingKey({ grp: null, key: documentId }));
                                
                            return;
                        }
                    } catch (_) {
                        dispatch(unsetLoader()); 
                        dispatch(unsetAdmLoadingKey({ grp: null, key: documentId }));
                    }

                } else if (found && !found.hasAllMembers && typeof sideState.keyLoader[found.documentId ?? ''] === 'undefined') {

                    dispatch(setLoadingKey(found.documentId ?? ''));
                    dispatch(setLoader());
                    dispatch(setAdmLoadingKey(documentId));
                    try {
                        // apiGetGroup returns Group & { fleet: Fleet } | null directly (no .data wrapper)
                        const fullGroupResp = await apiGetGroup(found.documentId ?? '');
                        if (fullGroupResp?.documentId) {
                            dispatch(upsertGroup(fullGroupResp));
                            dispatch(unsetAdmLoadingKey({ grp: fullGroupResp, key: documentId }));
                            dispatch(unsetLoader());
                        } else {
                            // Fallback: cache with what we have from contextGroupMap
                            dispatch(upsertGroup(found));
                            dispatch(unsetAdmLoadingKey({ grp: found, key: documentId }));
                            dispatch(unsetLoader());
                        }
                        return fullGroupResp ?? found;

                    } catch (_) {
                        dispatch(unsetLoader());
                    }
                } else if (found?.documentId && !found.hasAllMembers && typeof sideState.keyLoader[found.documentId] !== 'undefined') {

                    return await sideState.keyLoader[found.documentId];
                } else if (!found && typeof sideState.admKeyLoader[documentId] !== 'undefined') {
                    const admGrp = await sideState.admKeyLoader[documentId];
                    return admGrp;
                }
                return findAdminById(documentId) as fullGroup;
            } catch (error) {
                logException(error, { documentId });
                return [];
            }
        },
        async getById(documentId: string, force: boolean = false) {
            if (!documentId) return null;
            // Deduplicate: if a request is already in flight, wait for it
            if (typeof sideState.keyLoader[documentId] !== 'undefined') {
                return await sideState.keyLoader[documentId];
            }
            // Return from cache if fully loaded and not forced
            if (groupStoreState.contextGroupMap[documentId]?.hasAllMembers && !force) {
                return findById(documentId);
            }
            dispatch(setLoadingKey(documentId));
            dispatch(setLoader());
            try {
                // apiGetGroup returns Group & { fleet: Fleet } | null directly (no .data wrapper)
                const groupResp = await apiGetGroup(documentId);
                if (groupResp?.documentId) {
                    dispatch(upsertGroup(groupResp));
                    dispatch(unsetLoader());
                    return groupResp;
                } else {
                    dispatch(unsetLoader());
                }
            } catch (_) {
                dispatch(unsetLoader());
            }
            return findById(documentId);
        },
        async inheritedGroupBySuper(id: string) {
            const key = String(id);
            // Deduplicate: if a request is already in flight, wait for it
            if (typeof sideState.inheritanceLoader[key] !== 'undefined') {
                return await sideState.inheritanceLoader[key];
            }
            // Return from cache if available
            const cached = getInheritedGroupBySuper(id);
            if (cached.length > 0) {
                return cached;
            }
            // Fetch from API
            const loader = createEmptyPromise<fullGroup[]>();
            sideState.inheritanceLoader[key] = loader;
            dispatch(setLoader());
            try {
                const groupsResp = await apiGetSubGroupsByInheritanceRT(id);
                if (groupsResp.status.code < 400) {
                    dispatch(upsertGroups(groupsResp.data));
                    dispatch(unsetLoader());
                    loader.resolve(groupsResp.data as unknown as fullGroup[]);
                    delete sideState.inheritanceLoader[key];
                    return groupsResp.data;
                } else {
                    dispatch(unsetLoader());
                }
            } catch (_) {
                dispatch(unsetLoader());
            }
            loader.resolve([]);
            delete sideState.inheritanceLoader[key];
            return getInheritedGroupBySuper(id);
        },
        async preLoadAdminGroups() {
            if (!groupStoreState.preloader.parkstorySuperAdmin) {
                dispatch(setPreLoading({ key: 'admins', value: [] }));
                const groupsResp = await apiGetAllGroupAdminsRT();
                if (groupsResp) {
                    dispatch(upsertGroups(groupsResp));
                    dispatch(setPreLoading({ key: 'admins', value: groupsResp }));
                    return groupsResp;
                }
            }
            return groupStoreState.preloader.admins;

        },
        async preLoadSuperAdmin() {
            if (groupStoreState.preloader.parkstorySuperAdmin) {
                return groupStoreState.preloader.parkstorySuperAdmin;
            }
            if (sideState.superAdminLoader) {
                return sideState.superAdminLoader;
            }
            sideState.superAdminLoader = (async () => {
                try {
                    dispatch(setPreLoading({ key: 'parkstorySuperAdmin', value: [] }));
                    const groupsResp = await apiGetGroupsByPrefix('_admin');
                    if (groupsResp.status.code < 400) {
                        dispatch(upsertGroups(groupsResp.data));
                        dispatch(setPreLoading({ key: 'parkstorySuperAdmin', value: groupsResp.data }));
                        return groupsResp.data;
                    }
                    return null;
                } finally {
                    sideState.superAdminLoader = null;
                }
            })();
            return sideState.superAdminLoader;
        }

    };
}